import FadeIn from "./FadeIn";
import { C } from "./brandTokens";

const companions = [
  {
    label: "The Pastor",
    tagline: "Comfort. Hope. Daily application.",
    desc: "The Pastor meets you with warmth and scripture that speaks to where you are right now. No theology prerequisites. No performance required. Just honest pastoral care.",
    voice: "You named something real today — that feeling of smallness in the silence. That's not weakness. That's a heart that still expects something from the people it loves.",
    tier: "Free & Devoted",
    accentBdr: "rgba(107,125,86,0.50)",   // sage
  },
  {
    label: "The Theologian",
    tagline: "Context. Exegesis. Historical depth.",
    desc: "The Theologian brings the full weight of Christian tradition to bear on your reflection — original language, historical context, patristic commentary — without losing sight of the personal.",
    voice: "The Greek here is remarkable — Jesus uses a continuous tense. He is not saying he will give water once, but that he will keep giving it. This changes what thirst means in the passage.",
    tier: "Devoted",
    accentBdr: "rgba(106,88,120,0.45)",   // purple
  },
  {
    label: "The Spiritual Psychologist",
    tagline: "Inner patterns. Healing. Emotional honesty.",
    desc: "The Spiritual Psychologist holds scripture and psychology together — helping you see the emotional patterns underneath your spiritual questions, without pathologizing faith.",
    voice: "Notice what you're doing here — you're framing a relational wound as a question about yourself. That's a protective move. What would it look like to stay with the hurt a little longer before explaining it?",
    tier: "Devoted",
    accentBdr: "rgba(150,111,44,0.45)",   // gold
  },
];

export default function CompanionsSection() {
  return (
    <section className="py-[120px] px-6 sm:px-10" style={{ background: C.warm }}>
      <div className="max-w-[720px] mx-auto">

        {/* Section heading */}
        <FadeIn className="mb-[80px]">
          <h2
            className="font-heading font-semibold text-spec-brown mb-5"
            style={{ fontSize: "clamp(36px, 4.5vw, 58px)", lineHeight: 1.1 }}
          >
            Choose the voice that
            <br />
            speaks to your season.
          </h2>
          <p className="font-ui text-[16px] text-spec-txt2 leading-[1.75]" style={{ maxWidth: 480 }}>
            Three distinct companions — each one a different kind of spiritual presence.
            Switch anytime. Start with the Pastor for free. Built to work alongside
            your church community, not in place of it.
          </p>
        </FadeIn>

        {/* Companions — editorial rows */}
        <div>
          {companions.map((c, i) => (
            <FadeIn key={i} delay={i * 0.1}>
              <div
                style={{
                  paddingBottom: 64,
                  marginBottom: i < companions.length - 1 ? 0 : 0,
                  borderBottom: i < companions.length - 1 ? `1px solid ${C.bdr}` : "none",
                  paddingTop: i > 0 ? 64 : 0,
                }}
              >
                {/* Label row */}
                <div className="flex items-baseline gap-4 mb-3 flex-wrap">
                  <h3
                    className="font-heading font-semibold text-spec-brown"
                    style={{ fontSize: "clamp(22px, 2.5vw, 28px)", lineHeight: 1.2 }}
                  >
                    {c.label}
                  </h3>
                  <span
                    className="font-ui"
                    style={{ fontSize: 12, color: C.txt3, letterSpacing: "0.5px" }}
                  >
                    {c.tagline}
                  </span>
                  {c.tier !== "Free & Devoted" && (
                    <span
                      className="font-ui"
                      style={{ fontSize: 11, color: C.gold, letterSpacing: "0.8px" }}
                    >
                      Devoted
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="font-ui text-[15px] text-spec-txt2 leading-[1.75] mb-6">
                  {c.desc}
                </p>

                {/* Voice sample as blockquote — border color mirrors the companion's identity */}
                <blockquote
                  className="font-heading italic text-spec-brown"
                  style={{
                    fontSize: "clamp(16px, 1.8vw, 19px)",
                    lineHeight: 1.8,
                    borderLeft: `2px solid ${c.accentBdr}`,
                    paddingLeft: 20,
                    margin: 0,
                  }}
                >
                  "{c.voice}"
                </blockquote>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Trust + CTA — second emotional peak */}
        <FadeIn delay={0.3}>
          <div className="mt-16 pt-14" style={{ borderTop: `1px solid ${C.bdr}` }}>
            <p
              className="font-ui text-[14px] text-spec-txt3 leading-[1.8]"
              style={{ maxWidth: 520 }}
            >
              Your journal is yours. Eden Pages uses AI to reflect your own words back
              through the lens of scripture — the way a thoughtful spiritual director
              might. It doesn't replace your relationship with God. It helps you show
              up to it. We never read your entries, sell your data, or use your words
              to train AI models.
            </p>
          </div>
        </FadeIn>

      </div>
    </section>
  );
}
